import { useState } from "react";
import { Card, ConfirmDialog, ErrorState, PageHeader, SkeletonList, ToggleRow, showToast } from "@teamna/ui/components/index.ts";
import { isApiError } from "@teamna/web-client/api/client.ts";
import { formatDateTime } from "@teamna/web-client/lib/format.ts";
import { usePlatformSettings, useSetCardPayments, useSetOwnerSignUp } from "../lib/cc-api.ts";

/** "Changed 1 Nov by x@teamna.app.", or the default when nobody changed it. */
function changedText(sw: { updatedAt: string | null; updatedByStaffEmail: string | null }): string {
  return sw.updatedAt
    ? `Changed ${formatDateTime(sw.updatedAt)}${sw.updatedByStaffEmail ? ` by ${sw.updatedByStaffEmail}` : ""}.`
    : "Never changed. On is the default.";
}

/**
 * Teamna-wide switches: card payments (Stripe Connect) for all businesses, and owner sign-up.
 *
 * Owner sign-up off is early access only: a new business needs a number or an email on the allow
 * list (Early access page), and the website shows "Ask for early access" in place of "Start free
 * trial".
 *
 * Off: owners see "Coming soon" on Get paid, card payments, payment links, "ask for an amount" and
 * recurring fees; members see the pay in person path; the API refuses every card checkout. Cash and
 * bank transfer payments keep working. Each change asks for a confirm, because it changes every
 * business at once.
 */
export function PlatformSettingsScreen() {
  const settings = usePlatformSettings();
  const setCardPayments = useSetCardPayments();
  const setOwnerSignUp = useSetOwnerSignUp();
  const [asking, setAsking] = useState<boolean | null>(null);
  const [askingSignUp, setAskingSignUp] = useState<boolean | null>(null);

  if (!settings.data) {
    return (
      <>
        <PageHeader title="Settings" />
        {settings.isLoading ? (
          <output aria-label="Loading settings" className="block">
            <SkeletonList rows={1} />
          </output>
        ) : (
          <ErrorState error={settings.error} onRetry={() => void settings.refetch()} />
        )}
      </>
    );
  }

  const card = settings.data.cardPayments;
  const changed = changedText(card);
  const signUp = settings.data.ownerSignUp;

  const confirmSignUp = () => {
    if (askingSignUp === null) return;
    setOwnerSignUp.mutate(askingSignUp, {
      onSuccess: (data) => {
        setAskingSignUp(null);
        showToast(data.ownerSignUp.on ? "Sign-up is open to all." : "Sign-up is early access only.");
      },
      onError: (error) => showToast(isApiError(error) && error.detail ? error.detail : "The switch did not change. Try again."),
    });
  };

  const confirm = () => {
    if (asking === null) return;
    setCardPayments.mutate(asking, {
      onSuccess: (data) => {
        setAsking(null);
        showToast(data.cardPayments.on ? "Card payments are on." : "Card payments are off. Owners see \"Coming soon\".");
      },
      onError: (error) => showToast(isApiError(error) && error.detail ? error.detail : "The switch did not change. Try again."),
    });
  };

  return (
    <>
      <PageHeader title="Settings" description="These switches change Teamna for all businesses." />
      <Card className="max-w-[640px] px-4">
        <ToggleRow
          label="Card payments (Stripe)"
          hint={
            card.on
              ? `On. Owners can connect Stripe and members can pay by card. ${changed}`
              : `Off. Owners see "Coming soon" for all card payment parts. Cash and bank transfer payments still work. ${changed}`
          }
          checked={card.on}
          disabled={setCardPayments.isPending}
          onChange={(next) => setAsking(next)}
        />
        <ToggleRow
          label="Owner sign-up open to all"
          hint={
            signUp.on
              ? `On. Any person can create a business. The website shows "Start free trial". ${changedText(signUp)}`
              : `Off: early access only. Only a person on the Early access list can create a business. The website shows "Ask for early access". ${changedText(signUp)}`
          }
          checked={signUp.on}
          disabled={setOwnerSignUp.isPending}
          onChange={(next) => setAskingSignUp(next)}
        />
      </Card>
      <ConfirmDialog
        open={asking !== null}
        title={asking ? "Turn card payments on?" : "Turn card payments off?"}
        confirmLabel={asking ? "Turn on" : "Turn off"}
        tone={asking ? "primary" : "danger"}
        loading={setCardPayments.isPending}
        onConfirm={confirm}
        onCancel={() => setAsking(null)}
      >
        {asking
          ? "Owners can connect Stripe again on Get paid. Members of a business with a verified Stripe account can pay by card."
          : "All businesses change now. Owners see \"Coming soon\" on Get paid, payment links, \"Ask for an amount\" and recurring fees. Members cannot pay by card and see how to pay in person. Teamna sends no recurring fee requests. Cash and bank transfer payments still work."}
      </ConfirmDialog>
      <ConfirmDialog
        open={askingSignUp !== null}
        title={askingSignUp ? "Open sign-up to all?" : "Make sign-up early access only?"}
        confirmLabel={askingSignUp ? "Open sign-up" : "Early access only"}
        tone={askingSignUp ? "primary" : "danger"}
        loading={setOwnerSignUp.isPending}
        onConfirm={confirmSignUp}
        onCancel={() => setAskingSignUp(null)}
      >
        {askingSignUp
          ? "Any person can create a business again. The website shows \"Start free trial\" again. The website changes within about two minutes."
          : "Only a person on the Early access list can create a business. Owners of a business can still add another. The website shows \"Ask for early access\", and its sign-up link opens the early access form. Sign-in and invites still work. The website changes within about two minutes."}
      </ConfirmDialog>
    </>
  );
}
